/**
 * useReportLoader - Custom hook for loading report data when resuming
 * 
 * Handles:
 * - Fetching report data from API
 * - Converting month names to numbers
 * - Merging financial data formats
 * - Determining correct resume step
 */

import { useEffect, useCallback } from 'react';
import { useAuth } from '../../../../contexts/TestAuthContext';

interface ReportLoaderOptions {
  resumeReportId: string | null;
  setData: (data: any) => void;
  setStep: (step: number) => void;
  setHighestCompletedStep: (fn: (prev: number) => number) => void;
}

// Helper function to convert month names to numbers
const monthNameToNumber: Record<string, number> = {
  'january': 1, 'jan': 1,
  'february': 2, 'feb': 2,
  'march': 3, 'mar': 3,
  'april': 4, 'apr': 4,
  'may': 5,
  'june': 6, 'jun': 6,
  'july': 7, 'jul': 7,
  'august': 8, 'aug': 8,
  'september': 9, 'sep': 9, 'sept': 9,
  'october': 10, 'oct': 10,
  'november': 11, 'nov': 11,
  'december': 12, 'dec': 12
};

function parseMonthKey(key: string): { year: number; month: number } | null {
  // Try parsing formats like "2020-01", "2020-1", "January 2020", "Jan 2020"
  const dashMatch = key.match(/^(\d{4})-(\d{1,2})$/);
  if (dashMatch) {
    return { year: parseInt(dashMatch[1]), month: parseInt(dashMatch[2]) };
  }
  
  const wordMatch = key.match(/^([a-zA-Z]+)\s*(\d{4})$/);
  if (wordMatch) {
    const monthNum = monthNameToNumber[wordMatch[1].toLowerCase()];
    if (monthNum) {
      return { year: parseInt(wordMatch[2]), month: monthNum };
    }
  }
  
  return null;
}

function normalizeFinancialData(financialData: Record<string, any>): Record<string, any> {
  const normalized: Record<string, any> = {};
  
  for (const [key, value] of Object.entries(financialData)) {
    const parsed = parseMonthKey(key);
    if (parsed) {
      const normalizedKey = `${parsed.year}-${String(parsed.month).padStart(2, '0')}`;
      normalized[normalizedKey] = value;
    } else {
      // Keep keys that don't match (like 'data_2020', 'data_2021', etc.)
      normalized[key] = value;
    }
  }
  
  return normalized;
}

export function useReportLoader({
  resumeReportId,
  setData,
  setStep,
  setHighestCompletedStep
}: ReportLoaderOptions) {
  const { getToken } = useAuth();
  
  const loadReport = useCallback(async () => {
    if (!resumeReportId) return;
    
    console.log('📝 Loading report:', resumeReportId);
    
    try {
      const token = await getToken();
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
      
      // Use XMLHttpRequest to avoid rrweb-recorder issues with fetch
      // Try drafts endpoint first (for in-progress work), then reports endpoint
      const report = await new Promise<any>(async (resolve, reject) => {
        // First try drafts endpoint
        const tryEndpoint = (url: string): Promise<any> => {
          return new Promise((res, rej) => {
            const xhr = new XMLHttpRequest();
            xhr.open('GET', url);
            xhr.setRequestHeader('Authorization', `Bearer ${token}`);
            
            xhr.onload = () => {
              if (xhr.status >= 200 && xhr.status < 300) {
                try {
                  const data = JSON.parse(xhr.responseText);
                  // For drafts endpoint, data is nested under 'data' key
                  if (data.draft_id && data.data) {
                    // Flatten the structure for consistency
                    res({
                      ...data,
                      ...data.data,
                      id: data.draft_id
                    });
                  } else {
                    res(data);
                  }
                } catch (e) {
                  rej(new Error('Failed to parse report data'));
                }
              } else {
                rej(new Error(`HTTP ${xhr.status}`));
              }
            };
            
            xhr.onerror = () => rej(new Error('Network error'));
            xhr.send();
          });
        };
        
        try {
          // Try drafts endpoint first
          const draftData = await tryEndpoint(`${BACKEND_URL}/api/v1/reports/drafts/${resumeReportId}`);
          console.log('📦 Loaded from drafts endpoint');
          resolve(draftData);
        } catch (draftError) {
          console.log('⚠️ Drafts endpoint failed, trying reports endpoint');
          try {
            const reportData = await tryEndpoint(`${BACKEND_URL}/api/v1/reports/${resumeReportId}`);
            resolve(reportData);
          } catch (reportError) {
            reject(new Error(`Failed to load report from both endpoints`));
          }
        }
      });
      
      console.log('✅ Loaded existing report:', report);
      
      // Normalize financial data
      let financialDataToUse = report.financial_data || {};
      
      if (report.data?.financial_data && Object.keys(report.data.financial_data).length > 0) {
        financialDataToUse = {
          ...financialDataToUse,
          ...report.data.financial_data
        };
      }
      
      if (report.canonical_data?.periods) {
        for (const period of report.canonical_data.periods) {
          if (period.period_key && period.transactions) {
            financialDataToUse[period.period_key] = {
              transactions: period.transactions,
              ...period
            };
          }
        }
      }
      
      financialDataToUse = normalizeFinancialData(financialDataToUse);
      
      // Set data
      setData({
        law_firm: report.law_firm || report.data?.law_firm || '',
        client_name: report.client_name || report.data?.client_name || '',
        claimant_name: report.claimant_name || report.data?.claimant_name || '',
        claimant_email: report.claimant_email || report.data?.claimant_email || '',
        business_address: report.business_address || report.data?.business_address || '',
        zone: report.zone || report.data?.zone || '',
        business_type: report.business_type || report.data?.business_type || '',
        event_type: report.event_type || report.data?.event_type || '',
        event_description: report.event_description || report.data?.event_description || '',
        event_start_date: report.event_start_date || report.data?.event_start_date || '',
        event_end_date: report.event_end_date || report.data?.event_end_date || '',
        calculation_mode: report.calculation_mode || report.data?.calculation_mode || 'standard',
        financial_data: financialDataToUse,
        // Include unified_upload_data for COGS and other aggregated data
        unified_upload_data: report.unified_upload_data || report.data?.unified_upload_data || null,
        // Include cached_anomaly_analysis
        cached_anomaly_analysis: report.cached_anomaly_analysis || report.data?.cached_anomaly_analysis || null,
        // Include event_intelligence for EI banners
        event_intelligence: report.event_intelligence || report.data?.event_intelligence || null,
        tax1065Files: report.tax1065Files || [],
        plFiles: report.plFiles || [],
        uploadedDocuments: report.uploadedDocuments || [],
        // Load payroll fields dynamically for all years (not just 2019-2022)
        ...(() => {
          const payrollFields: Record<string, any> = {};
          for (let year = 2015; year <= 2026; year++) {
            const pKey = `payroll_${year}`;
            const epKey = `employee_payroll_${year}`;
            const ocKey = `officer_compensation_${year}`;
            const pkKey = `partner_k1_income_${year}`;
            if (report[pKey] !== undefined) payrollFields[pKey] = report[pKey];
            if (report[epKey] !== undefined) payrollFields[epKey] = report[epKey];
            if (report[ocKey] !== undefined) payrollFields[ocKey] = report[ocKey];
            if (report[pkKey] !== undefined) payrollFields[pkKey] = report[pkKey];
          }
          return payrollFields;
        })(),
        threshold: report.threshold || 0,
        threshold_type: report.threshold_type || 'dollar',
        accountant_fees: report.accountant_fees || 0,
        continuing_expenses: report.continuing_expenses || 0,
        extra_expenses: report.extra_expenses || 0,
        adjustment_notes: report.adjustment_notes || '',
        id: report.id || resumeReportId
      });
      
      // Resume at the correct step
      const resumeStep = report.current_step || 1;
      console.log(`🔄 Resuming at step ${resumeStep}`);
      setStep(resumeStep);
      setHighestCompletedStep(prev => Math.max(prev, resumeStep));
      
      // Start at Step 5 if it's a draft that never calculated
      if (report.status === 'draft' && !report.result) {
        setStep(5);
        setHighestCompletedStep(prev => Math.max(prev, 5));
      }
    } catch (error) {
      console.error('Failed to load report:', error);
      console.warn('Could not load report. Starting fresh.');
    }
  }, [resumeReportId, getToken, setData, setStep, setHighestCompletedStep]);
  
  useEffect(() => {
    loadReport();
  }, [loadReport]);
  
  return { loadReport };
}
